import { paginatedQuery } from 'lib-shared-api-utils';
import {
  ApiResponse,
  CreateQueueAgentsApiArg,
  CreateQueueApiArg,
  CreateQueueApiResponse,
  CreateQueueTeamsApiArg,
  DeleteQueueAgentsApiArg,
  DeleteQueueTeamApiArg,
  PatchQueueApiArg,
  QueueActivity,
  ReadQueueActivityLogApiArg,
  ReadQueueAgentsApiArg,
  ReadQueueAgentsApiResponse,
  ReadQueueApiArg,
  ReadQueueApiResponse,
  ReadQueuesApiArg,
  ReadQueuesApiResponse,
  ReadQueueTeamsApiArg,
  ReadQueueTeamsApiResponse,
  UpdateQueueAgentsApiArg,
  UpdateQueueApiArg,
  UpdateQueueApiResponse,
  UpdateQueueTeamsApiArg,
} from 'lib-shared-types-api';

import { transformReadQueueActivityLogResponse } from 'apis/queues/utils/transformResponse';
import { api } from 'app/app.api';

export const queuesApi = api
  .enhanceEndpoints({
    addTagTypes: ['Queue', 'QueueActivityLog'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      createQueue: builder.mutation<CreateQueueApiResponse, CreateQueueApiArg>({
        query: ({ queue }) => ({
          version: 2,
          path: 'queues',
          method: 'POST',
          body: queue,
        }),
      }),
      readQueue: builder.query<ReadQueueApiResponse, ReadQueueApiArg>({
        query: ({ id }) => ({
          version: 2,
          path: `queues/${id}`,
        }),
        providesTags: (_result, _error, arg) => [{ type: 'Queue', id: arg.id }],
      }),
      updateQueue: builder.mutation<UpdateQueueApiResponse, UpdateQueueApiArg>({
        query: ({ id, queue }) => ({
          version: 3,
          path: `queues/${id}`,
          method: 'PUT',
          body: queue,
        }),
        invalidatesTags: (_result, _error, arg) => [
          { type: 'Queue', id: arg.id },
          { type: 'QueueActivityLog', id: arg.id },
        ],
      }),
      patchQueue: builder.mutation<UpdateQueueApiResponse, PatchQueueApiArg>({
        query: ({ id, queue }) => ({
          version: 3,
          path: `queues/${id}`,
          method: 'PATCH',
          body: queue,
        }),
        invalidatesTags: (_result, _error, arg) => [
          { type: 'Queue', id: arg.id },
          { type: 'QueueActivityLog', id: arg.id },
        ],
      }),
      /**
       * @deprecated Use `searchApi.readSearchQueues` instead, or
       *   `use(Lazy)ReadSearchInfiniteQuery` hook for pagination support.
       */
      readQueues: builder.query<ReadQueuesApiResponse, ReadQueuesApiArg>({
        query: (arg) =>
          paginatedQuery(arg, (arg) => ({
            version: 3,
            path: 'queues',
            params: {
              is_archived: arg.isArchived,
              agent_id: arg.agentId,
              queue_id: arg.queueIds,
            },
          })),
      }),
      createQueueAgents: builder.mutation<ApiResponse, CreateQueueAgentsApiArg>(
        {
          query: ({ id, ...body }) => ({
            version: 2,
            path: `queues/${id}/agents`,
            method: 'PUT',
            body,
          }),
        },
      ),
      readQueueAgents: builder.query<
        ReadQueueAgentsApiResponse,
        ReadQueueAgentsApiArg
      >({
        query: (arg) =>
          paginatedQuery(arg, ({ id }) => ({
            version: 2,
            path: `queues/${id}/agents`,
          })),
      }),
      updateQueueAgents: builder.mutation<ApiResponse, UpdateQueueAgentsApiArg>(
        {
          query: ({ id, agents }) => ({
            version: 2,
            path: `queues/${id}/agents`,
            method: 'PUT',
            body: {
              agents,
            },
          }),
        },
      ),
      deleteQueueAgents: builder.mutation<ApiResponse, DeleteQueueAgentsApiArg>(
        {
          query: ({ id, agentIds: agents }) => ({
            version: 2,
            path: `queues/${id}/agents`,
            method: 'DELETE',
            body: { agents },
          }),
        },
      ),
      createQueueTeams: builder.mutation<ApiResponse, CreateQueueTeamsApiArg>({
        query: ({ id, ...body }) => ({
          version: 2,
          path: `queues/${id}/teams`,
          method: 'PUT',
          body,
        }),
      }),
      readQueueTeams: builder.query<
        ReadQueueTeamsApiResponse,
        ReadQueueTeamsApiArg
      >({
        query: (arg) =>
          paginatedQuery(arg, ({ id }) => ({
            version: 2,
            path: `queues/${id}/teams`,
          })),
      }),
      updateQueueTeams: builder.mutation<ApiResponse, UpdateQueueTeamsApiArg>({
        query: ({ id, teams }) => ({
          version: 2,
          path: `queues/${id}/teams`,
          method: 'PUT',
          body: {
            teams,
          },
        }),
      }),
      deleteQueueTeam: builder.mutation<ApiResponse, DeleteQueueTeamApiArg>({
        query: ({ id, teamId }) => ({
          version: 2,
          path: `queues/${id}/teams/${teamId}`,
          method: 'DELETE',
        }),
      }),
      // Implementation is a little different for this query in order to accommodate 'load more' functionality.
      // RTK docs: https://redux-toolkit.js.org/rtk-query/api/createApi#merge
      // Can be updated to native implementation once it's implemented in RTK: https://github.com/reduxjs/redux-toolkit/discussions/1163
      readQueueActivityLog: builder.query<
        QueueActivity,
        ReadQueueActivityLogApiArg
      >({
        query: ({ id, limit, order, last_key }) => ({
          version: 2,
          path: `queues/${id}/audit_log`,
          params: {
            limit: limit || 50,
            order: order || 'desc',
            last_key: last_key,
          },
        }),
        transformResponse: transformReadQueueActivityLogResponse,
        providesTags: (_result, _error, arg) => [
          { id: arg.id, type: 'QueueActivityLog' },
        ],
        // One cache for each queue and sort order combination
        serializeQueryArgs: ({ endpointName, queryArgs }) => {
          return `${endpointName}-queue-${queryArgs.id}-${queryArgs.order}`;
        },
        // Append new results if last_key is set, because if there is no last_key it is the initial load
        // update last_key
        merge: (currentCache, newItems, otherArgs): QueueActivity => {
          return {
            activityLog: [
              ...(otherArgs.arg.last_key ? currentCache.activityLog : []),
              ...newItems.activityLog,
            ],
            lastKey: newItems.lastKey,
          };
        },
        forceRefetch({ currentArg, previousArg }) {
          // Don't fetch more when 'order' changes, just read from a different cache
          return (
            currentArg?.id !== previousArg?.id ||
            currentArg?.limit !== previousArg?.limit ||
            currentArg?.last_key !== previousArg?.last_key
          );
        },
      }),
    }),
  });
